import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiClock,
  FiTag,
  FiUser,
  FiArrowLeft,
  FiSave
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";

const CreateShift = () => {
  const navigate = useNavigate();
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/shift`;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const initialShift = {
    name: "",
    code: "P",
    start_time: "08:00",
    end_time: "17:00",
  };

  const [shift, setShift] = useState(initialShift);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShift({ ...shift, [name]: value });
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!shift.name.trim() || !shift.code || !shift.start_time || !shift.end_time) {
      Swal.fire({
        title: "Form Belum Lengkap",
        text: "Semua field nama, kode, jam mulai, dan jam pulang wajib diisi!",
        icon: "warning",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_URL, shift, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        await Swal.fire({
          title: "Berhasil Ditambahkan!",
          text: `Shift "${shift.name}" berhasil dibuat.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        navigate("/enterprise/shift");
      }
    } catch (error) {
      console.error("Error create shift:", error);
      Swal.fire({
        title: "Gagal Membuat Shift",
        text: error.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enterprise/shift")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Shift Baru
            </h1>
            <p className="text-xs text-gray-500">
              Isi data nama shift, kode, serta alokasi jam kerja karyawan
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-gray-200/50 border border-gray-100"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Shift */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-teal-600 w-4 h-4" />
                Nama Shift
              </label>
              <input
                type="text"
                name="name"
                value={shift.name}
                onChange={handleChange}
                placeholder="Contoh: Shift Pagi A / Shift Malam B"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Kode Shift */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiTag className="text-teal-600 w-4 h-4" />
                Kode Shift
              </label>
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  { code: "P", label: "Pagi (P)", color: "bg-emerald-500 text-white" },
                  { code: "S", label: "Siang (S)", color: "bg-amber-500 text-white" },
                  { code: "L", label: "Libur (L)", color: "bg-rose-500 text-white" },
                  { code: "N", label: "Malam (N)", color: "bg-indigo-500 text-white" },
                ].map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => setShift({ ...shift, code: item.code })}
                    className={`px-4 py-2 rounded-2xl text-xs font-extrabold transition-all shadow-xs ${
                      shift.code === item.code
                        ? `${item.color} shadow-md scale-105`
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Jam Mulai */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiClock className="text-teal-600 w-4 h-4" />
                Jam Masuk (Mulai)
              </label>
              <input
                type="time"
                name="start_time"
                value={shift.start_time}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                required
              />
            </div>

            {/* Jam Pulang */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiClock className="text-teal-600 w-4 h-4" />
                Jam Pulang (Selesai)
              </label>
              <input
                type="time"
                name="end_time"
                value={shift.end_time}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/enterprise/shift")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold shadow-lg shadow-teal-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{loading ? "Menyimpan..." : "Simpan Shift"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>

      {/* Access Denied Overlay */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Anda tidak memiliki akses untuk menambah shift baru.
            </p>
            <button
              onClick={() => navigate("/enterprise/shift")}
              className="w-full py-2.5 rounded-2xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700"
            >
              Kembali
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateShift;
